import React, {
    FC,
    memo,
    useCallback,
    useMemo,
    PropsWithChildren,
} from 'react';

import type {
    CheckboxSelectionCallbackParams,
    ColDef,
    IRowNode,
    ModelUpdatedEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';

// components
import AGTable from '../../../../components/ag-table/AGTable';
// helper
import styled from 'styled-components';

// models
import {
    LabelAlternateListModel,
    LabelAlternateModel,
    LabelWithVariantsModel,
} from '../../../../models/label.model';

interface AlternatesTableProps {
    isLoading: boolean;
    handleSelectionChanged: (event: SelectionChangedEvent<any>) => void;
    selectedRowData: LabelAlternateListModel;
    currentLabel?: LabelWithVariantsModel;
    currentAlternateTexts: string[];
}
const AlternatesTable: FC<PropsWithChildren<AlternatesTableProps>> = ({
    isLoading,
    handleSelectionChanged,
    selectedRowData,
    currentLabel,
    currentAlternateTexts,
}) => {
    const labelName = useMemo<string>(() => {
        return currentLabel ? currentLabel.label_name : '';
    }, [currentLabel]);
    const emptyStateText = currentLabel ? 'select a label' : '';

    const rowData = useMemo<LabelAlternateModel[]>(() => {
        const alternates = currentLabel ? currentLabel?.alternates : [];
        return alternates.map((item, index) => ({
            text: item,
            label_name: labelName,
            index: index,
            label_id: currentLabel ? currentLabel.id : -1,
        }));
    }, [currentLabel, labelName]);

    const onModelUpdated = useCallback(
        (event: ModelUpdatedEvent<LabelAlternateModel>) => {
            const nodesToSelect: IRowNode[] = [];
            event.api.forEachNode((node) => {
                if (
                    node.data &&
                    currentAlternateTexts.includes(node.data?.text)
                ) {
                    nodesToSelect.push(node);
                }
            });
            event.api.setNodesSelected({
                nodes: nodesToSelect,
                newValue: true,
            });
        },
        [currentAlternateTexts],
    );

    const columns = useMemo<ColDef<LabelAlternateModel>[]>(
        () => [
            {
                headerName: 'Label Alternates',
                field: 'text',
                width: 200,
                cellClass: 'scrollable-cell',
                sort: undefined,
                sortable: undefined,
            },
            {
                width: 40,
                sortable: false,
                checkboxSelection: (
                    params: CheckboxSelectionCallbackParams<LabelAlternateModel>,
                ) => {
                    return params.data?.text
                        ? !currentAlternateTexts.includes(params.data?.text)
                        : false;
                },
                showDisabledCheckboxes: true,
            },
        ],
        [currentAlternateTexts],
    );

    return (
        <StyledAGTable
            rowData={rowData}
            columns={columns}
            isDataLoading={isLoading}
            handleSelectionChange={handleSelectionChanged}
            selectedRowsRef={selectedRowData[labelName]}
            rowStyle={{
                borderBottom: 'none',
            }}
            emptyStateText={emptyStateText}
            customOnModelUpdated={onModelUpdated}
        />
    );
};

const StyledAGTable = styled(AGTable)({
    marginTop: 0,
    padding: 0,
    height: '424px',
    width: '424px',
});
export default memo(AlternatesTable);
